import type { Person, TaskDto } from '@bk/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, errorMessage } from '../../lib/api';
import { dateTime, humanize } from '../../lib/format';
import { useTenant, useTenantQuery } from '../../lib/useTenant';
import { Badge, Button, ErrorText, Select } from '../../ui';
import { WorkClaimControls } from './WorkClaimControls';

const RISK_LABEL: Record<string, string> = { esterna: 'verso l’esterno', modifica_interna: 'modifica sistemi interni', proposta: 'bozza', lettura: 'lettura' };

/**
 * Un task: chi, cosa, cosa comunicare a valle, chiusura manuale (con esito se previsto e con
 * conferma esplicita per le azioni verso l'esterno), segnalazione e riassegnazione.
 */
export function TaskCard({ task, showProcess = true }: { task: TaskDto; showProcess?: boolean }) {
  const { tenant, t, isManager } = useTenant();
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [canWork, setCanWork] = useState(false);
  const people = useTenantQuery<{ data: Person[] }>(['people'], '/people', { enabled: isManager && expanded });

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['tenant', tenant.id] });
  const close = useMutation({
    mutationFn: (outcome?: string) => api<TaskDto>(t(`/tasks/${task.id}/close`), { body: { ...(outcome ? { outcome } : {}), ...(note.trim() ? { note: note.trim() } : {}), ...(task.confirmation_required ? { confirmed } : {}) } }),
    onSuccess: refresh,
  });
  const assign = useMutation({ mutationFn: (personId: string) => api<TaskDto>(t(`/tasks/${task.id}/assign`), { body: { person_id: personId } }), onSuccess: refresh });
  const report = useMutation({
    mutationFn: () => api<void>(t(`/tasks/${task.id}/report`), { body: { note: note.trim() } }),
    onSuccess: () => setNote(''),
  });
  const error = close.error ?? assign.error ?? report.error;
  const open = task.status === 'aperta';
  const eligible = (people.data?.data ?? []).filter((p) => p.active && p.roles.includes(task.role));

  return (
    <article className={`rounded-xl border bg-white p-4 shadow-sm ${open ? 'border-black/10' : 'border-black/5 opacity-70'}`}>
      <div className="flex flex-wrap items-start gap-2">
        <div className="min-w-0 flex-1">
          <p className="font-medium">{task.title}</p>
          <p className="text-xs text-ink/55">
            {showProcess && <>{task.process_name} · </>}
            {humanize(task.role)} · {task.assignee ? task.assignee.name : <span className="text-amber-700">da assegnare</span>}
            {!open && <> · chiuso {dateTime(task.closed_at)}{task.closed_on_behalf ? ' dal responsabile' : ''}</>}
          </p>
        </div>
        {task.attempt > 1 && <Badge tone="amber">tentativo {task.attempt}</Badge>}
        {task.risk_class === 'esterna' && <Badge tone="red">{RISK_LABEL.esterna}</Badge>}
        {task.outcome && <Badge tone={task.outcome === 'fallito' ? 'red' : 'green'}>{task.outcome}</Badge>}
        {task.status === 'annullata' && <Badge>annullato</Badge>}
      </div>

      {task.instructions && open && (
        <details className="mt-3 text-sm">
          <summary className="cursor-pointer text-ink/60">Come si fa</summary>
          <p className="mt-1 whitespace-pre-line text-ink/80">{task.instructions}</p>
        </details>
      )}
      {task.message && (
        <div className="mt-3 rounded-lg bg-blue-50 px-3 py-2 text-sm text-blue-900">
          <span className="block text-xs font-medium uppercase tracking-wide text-blue-700">Da comunicare a mano</span>
          {task.message}
        </div>
      )}
      {task.note && <p className="mt-2 text-sm text-ink/70">Nota: {task.note}</p>}
      {open && <WorkClaimControls family="task" taskId={task.id} canClaim={task.can_close} onCanWorkChange={setCanWork} />}

      {open && task.can_close && (
        <div className="mt-3 space-y-3">
          <input
            className="w-full rounded-lg border border-black/15 px-3 py-2 text-sm outline-none focus:border-accent"
            placeholder="Nota facoltativa"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          {task.confirmation_required && (
            <label className="flex items-start gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-900">
              <input type="checkbox" className="mt-1" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
              Confermo di aver eseguito questa azione verso l’esterno.
            </label>
          )}
          <div className="flex flex-wrap gap-2">
            {task.outcomes.length ? (
              task.outcomes.map((o) => (
                <Button key={o} variant={o === 'fallito' ? 'ghost' : 'primary'} disabled={!canWork || close.isPending || (task.confirmation_required && !confirmed)} onClick={() => close.mutate(o)}>
                  {humanize(o)}
                </Button>
              ))
            ) : (
              <Button disabled={!canWork || close.isPending || (task.confirmation_required && !confirmed)} onClick={() => close.mutate(undefined)}>
                Fatto
              </Button>
            )}
            <Button variant="ghost" disabled={!note.trim() || report.isPending} onClick={() => report.mutate()} title="Registra un problema senza chiudere il task">
              Segnala problema
            </Button>
            {isManager && (
              <Button variant="ghost" onClick={() => setExpanded(!expanded)}>
                Riassegna
              </Button>
            )}
          </div>
          {report.isSuccess && <p className="text-xs text-ink/50">Problema registrato.</p>}
          {expanded && (
            <Select value="" onChange={(e) => e.target.value && assign.mutate(e.target.value)}>
              <option value="">Assegna a…</option>
              {eligible.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} ({p.open_tasks} aperti)
                </option>
              ))}
            </Select>
          )}
        </div>
      )}
      <div className="mt-2">
        <ErrorText>{error ? errorMessage(error) : ''}</ErrorText>
      </div>
    </article>
  );
}
